/**
 * Custom Hook for Dynamic Menu Management
 * 
 * Manages menu items based on dashboard data and user role
 * Provides dynamic menu generation with real-time data integration
 */

import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getMenuByRole, ROLES } from '../config/menuConfig';
import { selectCVODashboard, selectAuthorityDashboard } from '../store/slices/dashboardSlice';
import { getMenusFromAPI } from '../config/menuManager';

export const useDynamicMenu = (userRole) => {
  const cvoDashboard = useSelector(selectCVODashboard);
  const authorityDashboard = useSelector(selectAuthorityDashboard);

  const dynamicMenuItems = useMemo(() => {
    if (!userRole) return [];

    // Get dashboard data based on role
    const dashboardData = userRole === ROLES.CVO ? cvoDashboard.data : authorityDashboard.data;
    
    // Get base menu for the role
    const baseMenu = getMenuByRole(userRole);
    
    // For CVO: Handle assigned_menus for dynamic menu filtering
    if (userRole === ROLES.CVO && dashboardData) {
      const assignedMenus = dashboardData.assigned_menus;
      
      // If assigned_menus is empty array [], show all menu items
      if (Array.isArray(assignedMenus) && assignedMenus.length === 0) {
        // Show all menus - return baseMenu with enhancements
        return baseMenu.map(menuItem => {
          const enhancedItem = { ...menuItem };
          if (dashboardData.user) {
            enhancedItem.userInfo = dashboardData.user;
          }
          return enhancedItem;
        });
      }
      
      // If assigned_menus has items, filter by route comparison
      if (Array.isArray(assignedMenus) && assignedMenus.length > 0) {
        // Extract routes from assigned_menus
        const assignedRoutes = assignedMenus
          .map(menu => menu.route)
          .filter(Boolean)
          .map(route => String(route).toLowerCase().trim());
        
        // Filter baseMenu to show only items whose featurePermission matches assigned_menus routes
        const filteredMenu = baseMenu.filter(menuItem => {
          if (!menuItem.featurePermission) {
            return false; // Exclude items without featurePermission
          }
          
          // Compare menu's featurePermission with assigned_menus routes
          const menuPermission = String(menuItem.featurePermission || '').toLowerCase().trim();
          
          // Check if menu's featurePermission matches any assigned route
          return assignedRoutes.some(route => route === menuPermission);
        });
        
        // Return filtered menu with enhancements
        return filteredMenu.map(menuItem => {
          const enhancedItem = { ...menuItem };
          if (dashboardData.user) {
            enhancedItem.userInfo = dashboardData.user;
          }
          return enhancedItem;
        });
      }
    }
    
    // For Authority or if no assigned_menus data, use existing logic
    if (dashboardData && dashboardData.assigned_menus && Array.isArray(dashboardData.assigned_menus)) {
      return getMenusFromAPI(dashboardData);
    }

    // Fallback to static menu if no dynamic data
    
    // For CVO role, filter menus based on privileges
    // Logic: 
    // - If privileges are empty/null/undefined/empty array → show ALL menus
    // - If privileges have items (length > 0) → show ONLY menus matching those privileges
    // - If dashboard data is not loaded yet → show ALL menus (safe fallback)
    let filteredMenu = baseMenu;
    if (userRole === ROLES.CVO && dashboardData && dashboardData.user) {
      // Get privileges from multiple possible locations in the user object
      const userPrivileges = dashboardData.user.privileges || 
                            dashboardData.user.cvo_privileges || 
                            dashboardData.user.privilege_ids || 
                            dashboardData.user.cvo_privilege_ids || 
                            [];
      
      // Normalize privileges to handle different formats (objects with name/id, or primitives)
      let normalizedPrivileges = [];
      if (Array.isArray(userPrivileges) && userPrivileges.length > 0) {
        normalizedPrivileges = userPrivileges.map(p => {
          if (typeof p === 'object' && p !== null) {
            // Extract name or id from privilege object
            // Priority: name (for matching featurePermission) > featurePermission > permission > route > id
            return p.name || p.featurePermission || p.permission || p.route || p.id || null;
          }
          // If it's a primitive (string/number), use it directly
          return p;
        }).filter(Boolean); // Remove any null/undefined values
      }
      
      // If normalizedPrivileges.length === 0, show all menus (filteredMenu = baseMenu)
      // If normalizedPrivileges.length > 0, filter menus to show only matching ones
      if (normalizedPrivileges.length > 0) {
        // Filter menus based on featurePermission matching privilege names/IDs
        filteredMenu = baseMenu.filter(menuItem => {
          // If menu item has no featurePermission, exclude it (shouldn't happen, but safe fallback)
          if (!menuItem.featurePermission) {
            return false;
          }
          
          // Check if menu's featurePermission matches any of the user's privileges
          const menuPermission = String(menuItem.featurePermission || '').toLowerCase().trim();
          
          return normalizedPrivileges.some(privilegeIdentifier => {
            if (!privilegeIdentifier) return false;
            
            const privilegeStr = String(privilegeIdentifier).toLowerCase().trim();
            
            // Direct match (exact comparison)
            // This matches privilege names to menu featurePermissions
            return privilegeStr === menuPermission;
          });
        });
      }
      // If normalizedPrivileges.length === 0, filteredMenu remains as baseMenu (show all menus)
    }
    // If dashboardData is null/undefined or dashboardData.user is null/undefined, 
    // filteredMenu remains as baseMenu (show all menus - safe fallback)
    
    // Enhance static menu items with any available dashboard data
    return filteredMenu.map(menuItem => {
      const enhancedItem = { ...menuItem };

      // Add dynamic properties based on menu item type and dashboard data
      if (dashboardData) {
        switch (menuItem.id) {
          case 'inbox':
            // Add notification count if available
            if (dashboardData.notifications) {
              enhancedItem.notificationCount = dashboardData.notifications.unread_count || 0;
              enhancedItem.hasNotifications = enhancedItem.notificationCount > 0;
            }
            break;

          case 'all-complaints':
          case 'assigned-complaints':
            // Add complaint counts if available
            if (dashboardData.complaints) {
              enhancedItem.complaintCount = dashboardData.complaints.total || 0;
              enhancedItem.pendingCount = dashboardData.complaints.pending || 0;
              enhancedItem.hasComplaints = enhancedItem.complaintCount > 0;
            }
            break;

          case 'complaint-officers':
            // Add officer count if available
            if (dashboardData.officers) {
              enhancedItem.officerCount = dashboardData.officers.total || 0;
              enhancedItem.activeOfficers = dashboardData.officers.active || 0;
              enhancedItem.hasOfficers = enhancedItem.officerCount > 0;
            }
            break;

          case 'settings':
            // Add settings status if available
            if (dashboardData.settings) {
              enhancedItem.settingsConfigured = dashboardData.settings.configured || false;
              enhancedItem.hasSettings = Object.keys(dashboardData.settings).length > 0;
            }
            break;

          default:
            // For other menu items, check if they have any relevant data
            if (dashboardData[menuItem.id]) {
              enhancedItem.hasData = true;
              enhancedItem.data = dashboardData[menuItem.id];
            }
            break;
        }

        // Add user info if available
        if (dashboardData.user) {
          enhancedItem.userInfo = dashboardData.user;
        }
      }

      return enhancedItem;
    });
  }, [userRole, cvoDashboard.data, authorityDashboard.data]);

  // Get loading state
  const isLoading = useMemo(() => {
    if (userRole === ROLES.CVO) {
      return cvoDashboard.isLoading;
    }
    if (userRole === ROLES.AUTHORITY) {
      return authorityDashboard.isLoading;
    }
    return false;
  }, [userRole, cvoDashboard.isLoading, authorityDashboard.isLoading]);

  // Get error state
  const error = useMemo(() => {
    if (userRole === ROLES.CVO) {
      return cvoDashboard.error;
    }
    if (userRole === ROLES.AUTHORITY) {
      return authorityDashboard.error;
    }
    return null;
  }, [userRole, cvoDashboard.error, authorityDashboard.error]);

  // Get dashboard data
  const dashboardData = useMemo(() => {
    if (userRole === ROLES.CVO) {
      return cvoDashboard.data;
    }
    if (userRole === ROLES.AUTHORITY) {
      return authorityDashboard.data;
    }
    return null;
  }, [userRole, cvoDashboard.data, authorityDashboard.data]);

  return {
    menuItems: dynamicMenuItems,
    isLoading,
    error,
    dashboardData,
    hasData: !!dashboardData,
    hasDynamicMenus: !!(dashboardData && dashboardData.assigned_menus),
  };
};

export default useDynamicMenu;
